import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, User, Video, Phone, MessageSquare, ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import toast from 'react-hot-toast';

const NewAppointment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const doctorIdFromUrl = searchParams.get('doctorId');

  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    doctorId: doctorIdFromUrl || '',
    appointmentDate: '',
    startTime: '',
    endTime: '',
    duration: 30,
    type: 'consultation',
    mode: 'video',
    symptoms: [],
    notes: ''
  });

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (formData.doctorId && formData.appointmentDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [formData.doctorId, formData.appointmentDate, formData.duration]);

  useEffect(() => {
    if (selectedDoctor) {
      setFormData(prev => ({ ...prev, doctorId: selectedDoctor._id }));
    }
  }, [selectedDoctor]);

  const fetchDoctors = async () => {
    try {
      setLoadingDoctors(true);
      const response = await fetch(`${API_BASE_URL}/api/users/doctors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDoctors(data.doctors || []);
        
        // If doctorId is in URL, select that doctor
        if (doctorIdFromUrl) {
          const doctor = data.doctors?.find(d => d._id === doctorIdFromUrl);
          if (doctor) {
            setSelectedDoctor(doctor);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      toast.error('Failed to load doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!formData.doctorId || !formData.appointmentDate) return;

    try {
      setLoadingSlots(true);
      const dateStr = formData.appointmentDate;
      const response = await fetch(
        `${API_BASE_URL}/api/appointments/availability/${formData.doctorId}?date=${dateStr}&duration=${formData.duration}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.availableSlots || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || 'Failed to load available slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDoctorChange = (e) => {
    const doctorId = e.target.value;
    const doctor = doctors.find(d => d._id === doctorId);
    setSelectedDoctor(doctor || null);
    setFormData(prev => ({ ...prev, doctorId, startTime: '', endTime: '' }));
    setAvailableSlots([]);
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setFormData(prev => ({ ...prev, appointmentDate: date, startTime: '', endTime: '' }));
  };

  const handleSlotSelect = (slot) => {
    setFormData(prev => ({
      ...prev,
      startTime: slot.startTime,
      endTime: slot.endTime
    }));
  };

  const handleSymptomAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      setFormData(prev => ({
        ...prev,
        symptoms: [...prev.symptoms, e.target.value.trim()]
      }));
      e.target.value = '';
    }
  };

  const handleSymptomRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.doctorId) {
      toast.error('Please select a doctor');
      return;
    }

    if (!formData.appointmentDate) {
      toast.error('Please select a date');
      return;
    }

    if (!formData.startTime || !formData.endTime) {
      toast.error('Please select a time slot');
      return;
    }

    try {
      setSubmitting(true);

      // Format appointment date as ISO string
      const appointmentDateISO = new Date(formData.appointmentDate).toISOString();

      const requestBody = {
        doctorId: formData.doctorId,
        appointmentDate: appointmentDateISO,
        startTime: formData.startTime,
        endTime: formData.endTime,
        type: formData.type,
        mode: formData.mode,
        symptoms: formData.symptoms,
        notes: formData.notes ? { patient: formData.notes } : undefined
      };

      const response = await fetch(`${API_BASE_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Appointment booked successfully!');
        navigate('/appointments');
      } else {
        toast.error(data.message || 'Failed to book appointment');
        console.error('Booking error:', data);
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error('An error occurred while booking the appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'audio':
        return <Phone className="h-4 w-4" />;
      case 'chat':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Video className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/appointments"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Appointments
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Book New Appointment</h1>
          <p className="text-gray-600 mt-2">Schedule a consultation with a healthcare professional</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card space-y-6">
          {/* Doctor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Doctor <span className="text-red-500">*</span>
            </label>
            {loadingDoctors ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">Loading doctors...</span>
              </div>
            ) : (
              <select
                value={formData.doctorId}
                onChange={handleDoctorChange}
                className="input-field w-full"
                required
              >
                <option value="">Choose a doctor...</option>
                {doctors.map(doctor => (
                  <option key={doctor._id} value={doctor._id}>
                    {doctor.name} - {doctor.doctorProfile?.specialization || 'General Medicine'}
                  </option>
                ))}
              </select>
            )}
            {selectedDoctor && (
              <div className="mt-3 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-primary-600 mr-2" />
                  <div>
                    <p className="font-medium text-gray-900">{selectedDoctor.name}</p>
                    <p className="text-sm text-gray-600">
                      {selectedDoctor.doctorProfile?.specialization || 'General Medicine'}
                      {selectedDoctor.doctorProfile?.consultationFee && (
                        <span className="ml-2">
                          • ${selectedDoctor.doctorProfile.consultationFee} consultation fee
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Appointment Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Appointment Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="input-field w-full"
                required
              >
                <option value="consultation">Consultation</option>
                <option value="follow-up">Follow-up</option>
                <option value="emergency">Emergency</option>
                <option value="routine">Routine</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consultation Mode <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.mode}
                onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value }))}
                className="input-field w-full"
                required
              >
                <option value="video">Video Call</option>
                <option value="audio">Audio Call</option>
                <option value="chat">Chat</option>
              </select>
            </div>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appointment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.appointmentDate}
              onChange={handleDateChange}
              min={getMinDate()}
              className="input-field w-full"
              required
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (minutes)
            </label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                duration: parseInt(e.target.value),
                startTime: '',
                endTime: ''
              }))}
              className="input-field w-full"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>

          {/* Available Time Slots */}
          {formData.doctorId && formData.appointmentDate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Time Slot <span className="text-red-500">*</span>
              </label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-gray-600">Loading available slots...</span>
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSlotSelect(slot)}
                      className={`p-3 border-2 rounded-lg text-center transition-all ${
                        formData.startTime === slot.startTime
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                      }`}
                    >
                      <Clock className="h-4 w-4 mx-auto mb-1" />
                      <div className="text-sm font-medium">
                        {slot.startTime} - {slot.endTime}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    No available time slots for this date. Please select another date.
                  </p>
                </div>
              )}
              {formData.startTime && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Selected: <span className="font-medium">{formData.startTime} - {formData.endTime}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Symptoms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Symptoms (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter symptom and press Enter"
              onKeyDown={handleSymptomAdd}
              className="input-field w-full"
            />
            {formData.symptoms.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.symptoms.map((symptom, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                  >
                    {symptom}
                    <button
                      type="button"
                      onClick={() => handleSymptomRemove(index)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              placeholder="Any additional information you'd like to share..."
              className="input-field w-full"
              maxLength={500}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.notes.length}/500 characters
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <Link
              to="/appointments"
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting || !formData.doctorId || !formData.appointmentDate || !formData.startTime}
              className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Booking...
                </>
              ) : (
                'Book Appointment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewAppointment;

